import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  DataList,
  Flex,
  Heading,
  Table,
  Text,
} from '@radix-ui/themes'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  PamDocumentContent,
  PamDocumentSummary,
  PamDuplicateCandidate,
  PamPlayerStatus,
  PamPlayerVerificationStatus,
  PamVerificationDetail,
  VendorOutcome,
  VendorOutcomeCategory,
} from '@Generated/back-office-client/src'
import { ChevronRightIcon } from '@radix-ui/react-icons'
import { Routes } from '@Components/main-app/types'
import { formatDateToTimeZone } from '@Components/util/utils'
import { ReasonModal } from '@Components/common/reason-modal'
import { JsonView } from '@Components/common/json-view'
import { MaskedValue } from '@Components/common/masked-value'
import { getAccountStatusDisplay } from './pam-player-status-display'
import { PamVerificationStatusBadge } from './pam-verification-status-badge'
import { PamActionStatus, PamDecisionAction } from './types'
import { PamVerificationDocuments } from './pam-verification-documents'
import { PamVerificationDocumentPreview } from './pam-verification-document-preview'

const ACTION_LABEL: Record<PamDecisionAction, string> = {
  [PamDecisionAction.APPROVE]: 'Approve',
  [PamDecisionAction.DENY]: 'Deny',
  [PamDecisionAction.UNLOCK]: 'Unlock',
}

const adverseSignalCategories: VendorOutcomeCategory[] = [
  VendorOutcomeCategory.sanctions,
  VendorOutcomeCategory.watchlist,
  VendorOutcomeCategory.pep,
  VendorOutcomeCategory.adverse_media,
]

// The single decision-relevant summary of an adverse vendor-screening result. Shown both as a
// full-width banner on the detail page and inside the manual-override modal, so a non-technical
// reviewer sees the same plain-language warning (not the full vendor-outcome breakdown).
const AdverseHitCallout = ({ hits }: { hits: VendorOutcome[] }) => {
  return (
    <Callout.Root color="red" variant="soft">
      <Callout.Text>
        Vendor screening returned {hits.length > 1 ? 'adverse hits' : 'an adverse hit'}:
      </Callout.Text>
      {hits.map((hit, index) => (
        <Callout.Text key={index}>{hit.rawOutcome}</Callout.Text>
      ))}
    </Callout.Root>
  )
}

const formatAddress = (
  address: PamDuplicateCandidate['address'] | PamVerificationDetail['submittedPii']['address'],
): string => {
  const parts = [address?.line1, address?.line2, address?.city, address?.state, address?.zip]
  if (!address || !parts.some((part) => part?.trim())) {
    return '—'
  }
  const line2 = address.line2 ? `, ${address.line2}` : ''
  return `${address.line1}${line2}, ${address.city}, ${address.state} ${address.zip}`
}

export type PamVerificationDetailViewProps = {
  canWrite: boolean
  actionStatus: PamActionStatus
  onDecision: (action: PamDecisionAction, reason: string) => Promise<void>
  documents: PamDocumentSummary[]
  isUploading: boolean
  uploadError: string | null
  onUpload: (file: File) => void
  onDelete: (document: PamDocumentSummary) => void
  previewDocument: PamDocumentSummary | null
  previewContent: PamDocumentContent | null
  previewLoading: boolean
  onPreview: (document: PamDocumentSummary) => void
  onClosePreview: () => void
  detail?: PamVerificationDetail
  backHref?: string
}

export const PamVerificationDetailView = ({
  canWrite,
  actionStatus,
  onDecision,
  documents,
  isUploading,
  uploadError,
  onUpload,
  onDelete,
  previewDocument,
  previewContent,
  previewLoading,
  onPreview,
  onClosePreview,
  detail,
  backHref,
}: PamVerificationDetailViewProps) => {
  const [openAction, setOpenAction] = useState<PamDecisionAction | null>(null)

  if (detail === undefined) {
    return null
  }

  const isApproved =
    detail.status === PamPlayerVerificationStatus.approved ||
    detail.status === PamPlayerVerificationStatus.manually_approved
  const isDenied = detail.status === PamPlayerVerificationStatus.denied
  const isDeactivated = detail.accountStatus === PamPlayerStatus.deactivated
  const pii = detail.submittedPii

  const adverseHits = detail.vendorOutcomes.filter(
    (outcome) => adverseSignalCategories.includes(outcome.category) && !outcome.cleared,
  )

  const openModal = (action: PamDecisionAction) => {
    setOpenAction(action)
  }

  const submit = async (reason: string) => {
    if (openAction === null) {
      return
    }
    try {
      await onDecision(openAction, reason)
      setOpenAction(null)
    } catch {
      // Error surfaced to the user via actionStatus; keep the modal open to retry.
    }
  }

  const historyRows = detail.history.map((entry, index) => {
    return (
      <Table.Row key={`${entry.createdAt}-${index}`}>
        <Table.Cell>{formatDateToTimeZone(entry.createdAt)}</Table.Cell>
        <Table.Cell>
          {entry.fromStatus ?? '—'} → {entry.toStatus}
        </Table.Cell>
        <Table.Cell>{entry.changedBy}</Table.Cell>
        <Table.Cell>{entry.reason ?? entry.reasonCode ?? '—'}</Table.Cell>
        <Table.Cell>
          {entry.vendorRawResponse ? <JsonView data={entry.vendorRawResponse as object} /> : '—'}
        </Table.Cell>
      </Table.Row>
    )
  })

  return (
    <Flex direction="column" gap="5">
      <Flex align="center" justify="between">
        <Flex align="center" gap="3">
          <Heading size="6">{detail.displayName || detail.clerkUserId}</Heading>
          <PamVerificationStatusBadge status={detail.status} size="2" />
          {(() => {
            const accountDisplay = getAccountStatusDisplay(detail.accountStatus)
            return (
              <Badge size="2" color={accountDisplay.color} variant="soft">
                {accountDisplay.label}
              </Badge>
            )
          })()}
        </Flex>
        <Button asChild>
          <Link to={backHref ?? Routes.PLAYER_DETAILS.replace(':lookupId', detail.clerkUserId)}>
            Back
          </Link>
        </Button>
      </Flex>

      <Card>
        <DataList.Root>
          <DataList.Item>
            <DataList.Label>Email</DataList.Label>
            <DataList.Value>{detail.email ?? '—'}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Name (submitted)</DataList.Label>
            <DataList.Value>{`${pii.firstName} ${pii.lastName}`.trim() || '—'}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Date of birth</DataList.Label>
            <DataList.Value>{pii.dob}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>SSN (last 4)</DataList.Label>
            <DataList.Value>
              <MaskedValue value={pii.ssn4} label="SSN" />
            </DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Phone</DataList.Label>
            <DataList.Value>{pii.phone}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Address</DataList.Label>
            <DataList.Value>{formatAddress(pii.address)}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Attempts</DataList.Label>
            <DataList.Value>
              {detail.attemptCount} / {detail.maxAttempts}
            </DataList.Value>
          </DataList.Item>
        </DataList.Root>
      </Card>

      {(detail.duplicateCandidates.length > 0 ||
        detail.status === PamPlayerVerificationStatus.duplicate_review) && (
        <Box>
          <Heading size="4" mb="2">
            Potential duplicate accounts
          </Heading>
          {detail.duplicateCandidates.length === 0 ? (
            <Callout.Root color="gray" variant="soft">
              <Text size="2">
                Flagged as a potential duplicate, but no account currently shares these identity
                details — the matching account(s) may have been resolved or had their details
                edited.
              </Text>
            </Callout.Root>
          ) : (
            (() => {
              const showSsn = detail.duplicateCandidates.some((c) => c.ssn4 != null)
              return (
                <Table.Root variant="surface">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Date of birth</Table.ColumnHeaderCell>
                      {showSsn && <Table.ColumnHeaderCell>SSN (last 4)</Table.ColumnHeaderCell>}
                      <Table.ColumnHeaderCell>Address</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Account</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Verification</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {detail.duplicateCandidates.map((candidate) => {
                      const accountDisplay = getAccountStatusDisplay(candidate.accountStatus)
                      return (
                        <Table.Row key={candidate.lookupId}>
                          <Table.Cell>{candidate.displayName || '—'}</Table.Cell>
                          <Table.Cell>{candidate.email ?? '—'}</Table.Cell>
                          <Table.Cell>{candidate.dob ?? '—'}</Table.Cell>
                          {showSsn && (
                            <Table.Cell>
                              {candidate.ssn4 ? (
                                <MaskedValue value={candidate.ssn4} label="SSN" />
                              ) : (
                                '—'
                              )}
                            </Table.Cell>
                          )}
                          <Table.Cell>{formatAddress(candidate.address)}</Table.Cell>
                          <Table.Cell>
                            <Badge color={accountDisplay.color} variant="soft">
                              {accountDisplay.label}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <PamVerificationStatusBadge status={candidate.verificationStatus} />
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              asChild
                              size="1"
                              variant="soft"
                              color="gray"
                              style={{ whiteSpace: 'nowrap' }}>
                              <Link
                                to={Routes.PLAYER_DETAILS.replace(':lookupId', candidate.lookupId)}>
                                View
                                <ChevronRightIcon />
                              </Link>
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Root>
              )
            })()
          )}
        </Box>
      )}

      {adverseHits.length > 0 && <AdverseHitCallout hits={adverseHits} />}

      {canWrite && isDeactivated && !isApproved && (
        <Callout.Root color="orange" variant="soft">
          <Callout.Text>
            This account is deactivated, so its verification can&apos;t be approved. Reactivate the
            account from the{' '}
            <Link to={Routes.PLAYER_DETAILS.replace(':lookupId', detail.clerkUserId)}>
              player details page
            </Link>{' '}
            first.
          </Callout.Text>
        </Callout.Root>
      )}

      {canWrite && (
        <Flex gap="3">
          {!isApproved && (
            <Button disabled={isDeactivated} onClick={() => openModal(PamDecisionAction.APPROVE)}>
              Approve
            </Button>
          )}
          {!isApproved && !isDenied && (
            <Button color="red" variant="soft" onClick={() => openModal(PamDecisionAction.DENY)}>
              Deny
            </Button>
          )}
        </Flex>
      )}

      <PamVerificationDocuments
        documents={documents}
        canWrite={canWrite}
        isUploading={isUploading}
        uploadError={uploadError}
        onUpload={onUpload}
        onPreview={onPreview}
        onDelete={onDelete}
      />

      <Box>
        <Heading size="4" mb="2">
          History
        </Heading>
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>When</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Transition</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>By</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Vendor response</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{historyRows}</Table.Body>
        </Table.Root>
      </Box>

      {openAction !== null && (
        <ReasonModal
          title={`${ACTION_LABEL[openAction]} verification`}
          description="This action is recorded in the audit trail. A reason is required."
          confirmLabel={ACTION_LABEL[openAction]}
          isPending={actionStatus === PamActionStatus.PENDING}
          errorMessage={
            actionStatus === PamActionStatus.ERROR
              ? 'Something went wrong. Please try again.'
              : null
          }
          onCancel={() => setOpenAction(null)}
          onConfirm={submit}>
          {openAction === PamDecisionAction.APPROVE && adverseHits.length > 0 && (
            <AdverseHitCallout hits={adverseHits} />
          )}
        </ReasonModal>
      )}

      <PamVerificationDocumentPreview
        open={previewDocument !== null}
        fileName={previewDocument?.fileName ?? null}
        content={previewContent}
        isLoading={previewLoading}
        onClose={onClosePreview}
      />
    </Flex>
  )
}
